import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // i18next 사용
import { Product } from '../../../types/types';
import ProductCardProfile from '../../../components/ProductCardProfile';
import InfiniteScroll from 'react-infinite-scroll-component'; // InfiniteScroll import
import { instance } from '../../../api/axiosInstance';
import ReportModal from '../../products/reportModal';
import Modal from '../../../components/Modal';

// 포맷 함수
const formatNumber = (number: number | bigint) => {
  return number.toLocaleString();
};

export default function Profile() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(); // useTranslation 훅 사용

  // 상태 관리
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<string>('newest');
  const [hasMore, setHasMore] = useState<boolean>(true); // 더 로드할 상품이 있는지 여부
  const [page, setPage] = useState<number>(1); // 페이지 번호
  const [reportModal, setReportModal] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);

  // Function to open report modal
  const OpenreportModal = () => {
    setReportModal(true);
  };

  // Function to close report modal
  const closeReportModal = () => {
    setReportModal(false);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // 정렬 함수
  const getSortedProducts = (products: Product[]) => {
    return [...products].sort((a, b) => {
      switch (sortBy) {
        case 'priceLowToHigh':
          return a.price - b.price;
        case 'priceHighToLow':
          return b.price - a.price;
        case 'nameAZ':
          return a.name.localeCompare(b.name);
        case 'nameZA':
          return b.name.localeCompare(a.name);
        case 'salesHighToLow':
          return b.star - a.star; // 숫자로 비교
        case 'salesLowToHigh':
          return a.star - b.star; // 숫자로 비교
        case 'newest':
        default:
          return new Date(b.date).getTime() - new Date(a.date).getTime();
      }
    });
  };

  // 정렬 기준 변경 핸들러
  const handleSortChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSortBy(event.target.value);
    setProducts(getSortedProducts(products)); // 정렬된 상품 목록 업데이트
  };

  // 상품 호출 함수
  const fetchProducts = async () => {
    if (!hasMore) return; // 더 로드할 상품이 없으면 중단
    setLoading(true);
    try {
      const params: any = {
        start: (page - 1) * 20, // 페이지에 따라 시작 인덱스 변경
        count: 20, // 한 번에 20개씩 로드
        userName: id, // userName 필터 추가
      };

      const response = await instance.get('/api/products/filter', { params });
      const newProducts = response.data;

      // 새로운 상품이 0개면 더 로드할 상품이 없다고 설정
      if (newProducts.length === 0) {
        setHasMore(false);
      } else {
        setProducts((prevProducts) => {
          // 중복 상품을 제외한 상품만 추가
          const uniqueProducts = newProducts.filter(
            (newProduct: Product) =>
              !prevProducts.some((product) => product.id === newProduct.id)
          );
          return [...prevProducts, ...uniqueProducts];
        });
        setPage((prevPage) => prevPage + 1); // 페이지 번호 증가
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  // 처음에 호출
  useEffect(() => {
    if (id) {
      fetchProducts();
    }
  }, [id, sortBy]); // id나 정렬 기준이 변경될 때마다 호출

  const moreOptions = ['신고하기'];

  const [isMoreOpenMobile, setIsMoreOpenMobile] = useState(false);
  const dropdownRefMobile = useRef<HTMLDivElement>(null);
  const toggleUserDropdownMobile = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event bubbling to document
    setIsMoreOpenMobile((prev) => !prev); // 드롭다운 상태 토글
  };

  return (
    <section className="max-w-[2060px] w-full mx-auto mt-[58px] pb-[30px] items-center justify-center flex mobiel  mobile:overflow-hidden">
      <div className="w-[1440px] flex flex-col items-center mt-8">
        <div className="relative aspect-[4.8] mobile:w-full">
          {/* 배너 이미지 */}
          <img
            src={`${process.env.PUBLIC_URL}/product.png`}
            className="aspect-[4.8] w-[1440px] mobile:h-[280px] object-cover"
          />
          {/* 프로필 로고 */}

          {isMobile ? (
            <>
              <div className="mt-[-55px] px-4 z-10 relative">
                <img
                  src={`${process.env.PUBLIC_URL}/icon.png`}
                  className="w-[102px] h-[102px] object-cover rounded-full bg-white border-4 border-white"
                  alt="Profile Logo"
                />
              </div>
            </>
          ) : (
            <img
              src={`${process.env.PUBLIC_URL}/icon.png`}
              className="w-[150px] h-[150px] object-cover rounded-full absolute left-4 bottom-4 z-10"
              alt="Profile Logo"
            />
          )}

          {/* 크리에이터 설명란 */}
          {isMobile ? (
            <>
              <div className="px-4 mt-4">
                <p className="text-black text-[22px] font-bold">{id}</p>
                <p className="text-Silver tracking-wide text-xs">
                  {t(
                    'profile.description',
                    'This is a default vendor description.'
                  )}
                </p>
              </div>
            </>
          ) : (
            <div className="bg-black bg-opacity-60 absolute flex flex-col left-[180px] bottom-4 z-10 p-4 rounded-lg">
              <p className="text-white text-sm mb-1">
                {t(
                  'profile.description',
                  'This is a default vendor description.'
                )}
              </p>
              <p className="text-white text-lg font-bold">{id}</p>
            </div>
          )}

          {isMobile ? (
            <>
              <div
                className=" 
              absolute right-4 top-[240px] inline-block bg-white shadow-md cursor-pointer rounded-full px-[6px] py-[6px] border border-black/10 text-xs text-[#828282]
              "
              >
                <div ref={dropdownRefMobile}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={16}
                    height={16}
                    viewBox="0 0 24 24"
                    onClick={toggleUserDropdownMobile}
                    className="cursor-pointer"
                  >
                    <path
                      fill="#545454"
                      d="M5 10c-1.1 0-2 .9-2 2s.9 2 2 2s2-.9 2-2s-.9-2-2-2m14 0c-1.1 0-2 .9-2 2s.9 2 2 2s2-.9 2-2s-.9-2-2-2m-7 0c-1.1 0-2 .9-2 2s.9 2 2 2s2-.9 2-2s-.9-2-2-2"
                    ></path>
                  </svg>
                  {isMoreOpenMobile && (
                    <div className="absolute right-4 mt-4 bg-white border rounded-lg shadow-lg max-w-[150px] w-full">
                      <ul>
                        {moreOptions.map((option, index) => (
                          <li
                            key={index}
                            className="px-4 py-2 hover:bg-gray-100 text-left cursor-pointer"
                            onClick={OpenreportModal}
                          >
                            {option}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="md:flex md:absolute bottom-0 right-0 h-8 bg-black bg-opacity-60 w-1/12 justify-center items-center">
              <p
                className="text-sm font-bold text-white cursor-pointer"
                onClick={OpenreportModal}
              >
                신고하기
              </p>
            </div>
          )}
        </div>

        <Modal isOpen={reportModal} onClose={closeReportModal}>
          <ReportModal closeReportModal={closeReportModal} />
        </Modal>
        {/* 정렬 드롭다운 */}
        <div className="mt-4 w-full flex items-center mobile:px-4 mobile:flex-row-reverse">
          <h2 className="text-lg font-semibold mr-[20px] mobile:hidden ">
            {t('profile.sort')}
          </h2>
          <select
            value={sortBy}
            onChange={handleSortChange}
            className="bg-white border border-gray-300 rounded-md p-2 text-sm "
          >
            <option value="newest">{t('profile.newest')}</option>
            <option value="priceLowToHigh">
              {t('profile.priceLowToHigh')}
            </option>
            <option value="priceHighToLow">
              {t('profile.priceHighToLow')}
            </option>
            <option value="nameAZ">{t('profile.nameAZ')}</option>
            <option value="nameZA">{t('profile.nameZA')}</option>
            <option value="salesHighToLow">
              {t('profile.salesHighToLow')}
            </option>
            <option value="salesLowToHigh">
              {t('profile.salesLowToHigh')}
            </option>
          </select>
        </div>

        {/* 로딩 상태 표시 */}
        {loading && (
          <div className="mt-4 text-center">
            <p>{t('profile.loading', 'Loading products...')}</p>
          </div>
        )}

        {/* 무한 스크롤로 상품 목록 표시 */}
        <InfiniteScroll
          dataLength={products.length} // 스크롤이 작동할 데이터 길이
          next={fetchProducts} // 더 가져오는 함수
          hasMore={hasMore} // 더 가져올 수 있는지 여부
          loader={<h4>Loading...</h4>} // 로딩 중일 때 표시될 컴포넌트
          endMessage={<p>{t('profile.endOfResults', 'No more products.')}</p>} // 더 가져올 데이터가 없을 때
        >
          <div className="mt-4 pb-[60px] grid grid-cols-1 mobile:px-4 mobile:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-2">
            {products.length > 0 ? (
              getSortedProducts(products).map((product) => (
                <ProductCardProfile
                  key={product.id}
                  product={product}
                  formatNumber={formatNumber}
                />
              ))
            ) : (
              <p>{t('profile.noProducts', 'No products found.')}</p>
            )}
          </div>
        </InfiniteScroll>
      </div>
    </section>
  );
}
